import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Fab,
  Menu,
  MenuItem,
  Avatar,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { BRAND } from '../../components/auth/authTheme';
import ProtectedImage from '../../components/ProtectedImage';

const NAVY = BRAND.navy;
const ORANGE = BRAND.orange;
const BG = '#F8FAFC';

const EmployerCard = ({ employer, handleView, handleEdit, handleDelete, handleToggleActive, canEdit, canDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const fullName = `${employer.first_name || ""} ${employer.last_name || ""}`.trim();
  const initials = `${employer.first_name?.[0] || ""}${employer.last_name?.[0] || ""}`.toUpperCase();
  const company = employer.company?.company_name || '—';
  const email = employer.email || '—';
  const phone = employer.mobile_number || '—';

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '16px',
        p: 2,
        mb: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        border: `1px solid ${BRAND.border}`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: ORANGE, fontWeight: 600 }}>
            {initials || "?"}
          </Avatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {fullName}
              </Typography>
              <Switch
                size="small"
                checked={Boolean(employer.is_active)}
                onChange={(e) => handleToggleActive && handleToggleActive(employer, e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: ORANGE,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: ORANGE,
                  },
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: BRAND.muted, mt: 0.5, mb: 0.25 }}>
              <BusinessIcon sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{company}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: BRAND.muted, mb: 0.25 }}>
              <EmailIcon sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: BRAND.muted }}>
              <PhoneIcon sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{phone}</Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ ml: 1 }}>
          <IconButton size="small" onClick={handleMenuOpen} sx={{ color: BRAND.muted }}>
            <MoreVertIcon />
          </IconButton>
          
          <Menu 
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            MenuListProps={{
              sx: { display: 'flex', flexDirection: 'row', p: 0.5, gap: 0.5 }
            }}
            PaperProps={{
              elevation: 4,
              sx: {
                minWidth: 'auto',
                borderRadius: '24px',
                mt: 0.5,
              }
            }}
          >
            <MenuItem onClick={() => { handleMenuClose(); handleView(employer); }} sx={{ p: 1, minHeight: 'auto', borderRadius: '50%' }}>
              <VisibilityIcon fontSize="small" sx={{ color: '#2196f3' }} />
            </MenuItem>
            {canEdit && (
              <MenuItem onClick={() => { handleMenuClose(); handleEdit(employer); }} sx={{ p: 1, minHeight: 'auto', borderRadius: '50%' }}>
                <EditIcon fontSize="small" sx={{ color: '#4caf50' }} />
              </MenuItem>
            )}
            {canDelete && (
              <MenuItem onClick={() => { handleMenuClose(); handleDelete(employer.id); }} sx={{ p: 1, minHeight: 'auto', borderRadius: '50%' }}>
                <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

const EmployerMobileView = ({
  employers,
  searchTerm,
  setSearchTerm,
  handleView,
  handleEdit,
  handleDelete,
  handleToggleActive,
  openModalForCreate,
  canCreate,
  canEdit,
  canDelete,
}) => {
  return (
    <Box sx={{ pb: 8, pt: 1, px: 2, bgcolor: BG, minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
         <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: NAVY }}>
           Employers
         </Typography>
         <Typography sx={{ fontSize: '0.85rem', color: BRAND.muted }}>
           Manage organization employers and their access
         </Typography>
      </Box>

      <TextField
        fullWidth
        placeholder="Search employers..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2.5,
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: ORANGE, borderWidth: '1px' },
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          )
        }}
      />

      <Box sx={{ mt: 1 }}>
        {employers.length > 0 ? (
          employers.map((emp) => (
             <EmployerCard 
               key={emp.id}
               employer={emp}
               handleView={handleView}
               handleEdit={handleEdit}
               handleDelete={handleDelete}
               handleToggleActive={handleToggleActive}
               canEdit={canEdit}
               canDelete={canDelete}
             />
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
             <Typography sx={{ color: BRAND.muted, fontWeight: 500 }}>
               No employers found
             </Typography>
          </Box>
        )}
      </Box>

      {canCreate && (
        <Fab 
          color="primary" 
          aria-label="add" 
          onClick={openModalForCreate}
          size="small"
          sx={{
            position: 'fixed',
            bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))', // Above bottom nav
            right: 20,
            bgcolor: ORANGE,
            '&:hover': { bgcolor: BRAND.orangeHover },
            boxShadow: '0 4px 16px rgba(204,85,0,0.4)',
            zIndex: 1002,
          }}
        >
          <AddIcon sx={{ fontSize: '1.25rem' }} />
        </Fab>
      )}
    </Box>
  );
};

export default EmployerMobileView;
